import { UseQueryResult } from "@tanstack/react-query/src/types";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, UseMutationResult, useQuery } from "@tanstack/react-query";
import {
  createMembershipWithPackage,
  createOrderPurchase,
  createPackageOrder,
  getAvailableMembershipPackageDetail,
  getAvailableMembershipPackageList,
  getAvailableCreditPackageList,
  getAvailableServicePackageList,
  getPackageOrderDetail,
  updatePackageOrder,
  createOrderInstallment, updatePackageOrderDiscount,
} from "src/api/membership";

type IUseListAvailableMembershipPackageRequest = (
  data?: API.Packages.List.Request,
) => UseQueryResult<
  AxiosResponse<API.Packages.List.Response>,
  AxiosError<API.Packages.List.ErrorResponse>
>;

export const useListAvailableMembershipPackageRequest: IUseListAvailableMembershipPackageRequest = (data) => (
  useQuery(
    ["useListAvailableMembershipPackageRequest", data],
    async () => await getAvailableMembershipPackageList(data),
  )
);

export const useListAvailableServicePackageRequest: IUseListAvailableMembershipPackageRequest = (data) => (
  useQuery(
    ["useListAvailableServicePackageRequest", data],
    async () => await getAvailableServicePackageList(data),
  )
);

export const useListAvailableCreditPackageRequest: IUseListAvailableMembershipPackageRequest = (data) => (
  useQuery(
    ["useListAvailableCreditPackageRequest", data],
    async () => await getAvailableCreditPackageList(data),
  )
);

type IUseDetailAvailableMembershipPackageRequest = (
  id: API.Packages.PackageData["id"]
) => UseQueryResult<
  AxiosResponse<API.Packages.Detail.Response>,
  AxiosError<API.Packages.Detail.ErrorResponse>
>;

export const useDetailAvailableMembershipPackageRequest: IUseDetailAvailableMembershipPackageRequest = (id) => (
  useQuery(
    ["useDetailAvailableMembershipPackageRequest", id],
    async () => await getAvailableMembershipPackageDetail(id),
  )
);

type IUseCreateMembershipPurchaseRequest = (
  id: API.Packages.PackageData["id"]
) => UseMutationResult<
  AxiosResponse<API.Packages.Purchase.Response>,
  AxiosError<API.Packages.Purchase.ErrorResponse>,
  API.Packages.Purchase.Request
>;

export const useCreateMembershipPurchaseRequest: IUseCreateMembershipPurchaseRequest = (id) => (
  useMutation({
    mutationFn: async (data) => await createMembershipWithPackage(id, data),
  })
);

type IUseCreatePackageOrderRequest = () => UseMutationResult<
  AxiosResponse<API.Packages.Order.Response>,
  AxiosError<API.Packages.Order.ErrorResponse>,
  API.Packages.Order.Request & {
    id: API.Packages.PackageData["id"],
  }
>;

export const useCreatePackageOrderRequest: IUseCreatePackageOrderRequest = () => (
  useMutation({
    mutationFn: async ({ id, ...data }) => await createPackageOrder(id, data),
  })
);

type IUseUpdatePackageOrderRequest = (
  id: API.Packages.OrderData["id"],
) => UseMutationResult<
  AxiosResponse<API.Packages.UpdateOrder.Response>,
  AxiosError<API.Packages.UpdateOrder.ErrorResponse>,
  Partial<API.Packages.UpdateOrder.Request>
>;

export const useUpdatePackageOrderRequest: IUseUpdatePackageOrderRequest = (id) => (
  useMutation({
    mutationFn: async (data) => await updatePackageOrder(id, data),
  })
);

type IUseOrderDiscountRequest = () => UseMutationResult<
  AxiosResponse<API.Packages.OrderDiscount.Response>,
  AxiosError<API.Packages.OrderDiscount.ErrorResponse>,
  API.Packages.OrderDiscount.Request & {
    id: API.Packages.OrderData["id"],
  }
>;

export const useOrderDiscountRequest: IUseOrderDiscountRequest = () => (
  useMutation({
    mutationFn: async ({ id, ...data }) => await updatePackageOrderDiscount(id, data),
  })
);

type IUseCreateOrderPurchaseRequest = (
  data: API.Packages.OrderData["id"],
) => UseMutationResult<
  AxiosResponse<API.Packages.PurchaseOrder.Response>,
  AxiosError<API.Packages.PurchaseOrder.ErrorResponse>,
  API.Packages.PurchaseOrder.Request
>;

export const useCreateOrderPurchaseRequest: IUseCreateOrderPurchaseRequest = (id) => (
  useMutation({
    mutationFn: async (data) => await createOrderPurchase(id, data),
  })
);

type IUseCreateOrderInstallmentRequest = () => UseMutationResult<
  AxiosResponse<API.Packages.Installment.Response>,
  AxiosError<API.Packages.Installment.ErrorResponse>,
  API.Packages.OrderData["id"]
>;

export const useCreateOrderInstallmentRequest: IUseCreateOrderInstallmentRequest = () => (
  useMutation({
    mutationFn: async (id) => await createOrderInstallment(id),
  })
);

type IUseDetailPackageOrderRequest = (
  id: API.Packages.OrderData["id"],
) => UseQueryResult<
  AxiosResponse<API.Packages.Order.Response>,
  AxiosError<API.Packages.Order.ErrorResponse>
>;

export const useDetailPackageOrderRequest: IUseDetailPackageOrderRequest = (id) => (
  useQuery(
    ["useDetailPackageOrderRequest", id],
    async () => await getPackageOrderDetail(id),
  )
);
